<template>
  <div class="row">
    <div class="small-12 columns">
      <div class="callout">
        <h5>Material Selection</h5>

        <div class="row">
          <div class="small-12 large-2 columns">
            <metal-type 
              v-model="metalType" 
              class="font16"
              :authenticated="authenticated" 
              :activeShop="activeShop"
            />
          </div>

          <div class="small-12 large-2 columns">
            <product-form
              v-model="productForm"
              class="font16"
              :metal-type="metalType"
              :disabled="metalType === ''"
            />
          </div>

          <div class="small-12 large-2 columns">
            <grade-alloy
              v-model="gradeAlloy"
              class="font16"
              :metal-type="metalType"
              :product-form="productForm"
              :disabled="productForm === ''"
            />
          </div>

          <div class="small-12 large-6 columns">
            Please select Metal Type, Product Form, Grade/Alloy, and
            Specifications.<br />Once completed, please proceed to Product
            Configuration.
          </div>
        </div>

        <div class="row">
          <div class="small-12 large-6 columns">
            <specification
              v-model="specification"
              class="font16"
              :disabled="gradeAlloy === ''"
              :fabricated-product="fabricatedProductForm"
              :grade-alloy="gradeAlloy"
              :metal-type="metalType"
              :product-form="productForm"
            />
          </div>
        </div>
      </div>

      <div class="callout">
        <h5>Product Configuration</h5>

        <div class="row">
          <div class="small-12 large-2 columns">
            <primary-dimension
              v-model="primaryDimension"
              class="font16"
              :disabled="specification === ''"
              :fabricated-product="fabricatedProductForm"
              :grade-alloy="gradeAlloy"
              :metal-type="metalType"
              :product-form="productForm"
              :specification="specification"
            />
          </div>

          <div class="small-12 large-2 columns">
            <label class="font16">
              Quantity <span v-if="fabricatedProductForm">(Pieces)</span>
              <br />
              <input
                v-model.number="quantity"
                :disabled="size.type === 'custom'"
                id="config_quantity_input"
              />
            </label>
          </div>

          <div class="small-12 large-2 columns">
            <label class="font16">
              UOM
              <select
                v-model="uom"
                class="font16"
                :disabled="size.type === 'custom' || fabricatedProductForm"
                id="uom_input"
              >
                <option value="lbs">lbs</option>
                <option value="in">inch</option>
                <option value="ft">feet</option>
              </select>
            </label>
          </div>

          <div class="small-12 large-6 columns">
            Material Selection must be completed to access Product
            Configuration.
            <br />
            Not seeing the product you're looking for? Use our
            <a href="https://titanium.com/rfq" target="_blank">RFQ submittal form</a>.
          </div>
        </div>

        <div v-if="variant" class="row" style="margin-bottom: 10px">
          <div class="small-12 large-2 columns">
            <button
              class="button gray rounded"
              :class="{ green: stockSize }"
              @click="size.type = 'stock'"
            >
              Stock Size
            </button>
          </div>

          <div class="small-12 large-2 columns">
            <button
              :disabled="customCutDisabled"
              class="button gray rounded"
              :class="{ green: customSize }"
              @click="size.type = 'custom'"
            >
              Custom Cut
            </button>
          </div>

          <div class="small-12 large-4 columns">
            <span v-if="!fabricatedProductForm">
              T.I.'s Value Added Cutting Services
            </span>
          </div>
        </div>

        <div v-if="variant" class="row">
          <div class="small-12 large-2 columns">
            <p v-if="variant.attributes.depth > 0">
              <label>
                Length
                <br />
                {{ variant.attributes.depth }}"
                <span v-if="variant.attributes.tolerance_length">
                  ±{{ variant.attributes.tolerance_length }}"
                </span>
              </label>
            </p>

            <p v-if="variant.attributes.width > 0">
              <label>
                <span
                  v-if="
                    productForm.includes('Pipe') || productForm.includes('Tube')
                  "
                >
                  Wall
                </span>
                <span v-else>
                  Width
                </span>
                <br />
                {{ variant.attributes.width }}"
                <span v-if="variant.attributes.tolerance_width">
                  ±{{ variant.attributes.tolerance_width }}"
                </span>
              </label>
            </p>
          </div>

          <div class="small-12 large-2 columns">
            <template v-if="customSize">
              <p>
                <label class="font16">
                  Cut Length (Inches)
                  <input class="font16" v-model.number="size.depth" />
                </label>
              </p>

              <p
                v-if="
                  variant.attributes.width > 0 &&
                    !productForm.includes('Bar') &&
                    !productForm.includes('Pipe') &&
                    !productForm.includes('Tube')
                "
              >
                <label>
                  Cut Width (Inches)
                  <input v-model.number="size.width" />
                </label>
              </p>

              <p>
                <label class="font16">
                  Quantity (Pieces)
                  <br />
                  <input v-model.number="quantity" />
                </label>
              </p>
            </template>
          </div>
        </div>

        <div class="row">
          <div class="small-12 large-2 columns">
            <button
              type="button"
              class="button gray green rounded"
              style="padding: 0"
              :disabled="
                variant === null || validSize === false || Number(quantity) <= 0
              "
              @click="addItem"
            >
              + Add to Quote
            </button>

            <button
              type="button"
              class="button gray rounded"
              :disabled="metalType === ''"
              @click="resetForm"
            >
              Reset
            </button>
          </div>
        </div>

        <div class="row">
          <div class="small-12 large-2 columns" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import GradeAlloy from "./QuoteCreationTool/TheGradeAlloy";
import MetalType from "./QuoteCreationTool/TheMetalType";
import PrimaryDimension from "./QuoteCreationTool/ThePrimaryDimension";
import ProductForm from "./QuoteCreationTool/TheProductForm";
import Specification from "./QuoteCreationTool/TheSpecification";

export default {
  components: {
    GradeAlloy,
    MetalType,
    PrimaryDimension,
    ProductForm,
    Specification
  },
  props: {
    authenticated: {
      type: Boolean,
      required: true
    },
    activeShop: {
      type: Boolean,
      required: true
    },
    performResetForm: {
      type: Boolean,
      required: true
    }
  },
  data: function() {
    return {
      depth: 0,
      fabricatedProductNames: [
        "45 ° Elbow",
        "90 ° Elbow",
        "Concentric Reducer",
        "Eccentric Reducer",
        "Flange",
        "Stub Ends",
        "Tee"
      ],
      gradeAlloy: "",
      metalType: "",
      primaryDimension: "",
      productForm: "",
      quantity: null,
      size: {
        depth: 0,
        type: "stock",
        width: 0
      },
      specification: "",
      uom: "lbs",
      variant: null,
      width: 0
    };
  },
  computed: {
    customSize() {
      return this.size.type === "custom";
    },
    description() {
      return `${this.primaryDimension} ${this.metalType} ${this.gradeAlloy} ${this.productForm}`;
    },
    customCutDisabled() {
      return this.fabricatedProductForm || this.productForm === "Sheet";
    },
    fabricatedProductForm() {
      return this.fabricatedProductNames.includes(this.productForm);
    },
    stockSize() {
      return this.size.type === "stock";
    },
    validSize() {
      if (this.size.type === "stock") {
        return true;
      }

      if (this.size.type === "custom") {
        if (this.size.depth > 0) {
          if (
            this.productForm.includes("Bar") ||
            this.productForm.includes("Pipe") ||
            this.productForm.includes("Tube")
          ) {
            return true;
          } else {
            return this.size.width > 0;
          }
        } else {
          return false;
        }
      }

      return false;
    }
  },
  watch: {
    gradeAlloy() {
      this.specification = "";
      this.primaryDimension = "";
      this.quantity = null;
      this.variant = null;
    },
    metalType() {
      this.productForm = "";
      this.quantity = null;
    },
    primaryDimension() {
      this.quantity = null;
      if (this.primaryDimension !== "") {
        if (this.fabricatedProductForm) {
          this.getVariantFabricated();
        } else {
          this.getVariant();
        }
      } else {
        this.variant = null;
      }
    },
    productForm() {
      this.gradeAlloy = "";
      this.quantity = null;
    },
    performResetForm() {
      if (this.performResetForm) {
        console.log("resetting form");
        this.resetForm();
        this.$emit("form-is-reset");
      }
    },
    specification() {
      this.primaryDimension = "";
      this.quantity = null;
      this.variant = null;
    }
  },
  methods: {
    ...mapMutations(["setLoading"]),
    addItem() {
      if (this.quantity <= 0) {
        return;
      }

      const item = {
        quantity: Math.round(this.quantity),
        variant: this.variant
      };

      if (this.stockSize) {
        if (!this.fabricatedProductForm) {
          item.uom = this.uom;
        }

        this.$emit("add-item", item);
      } else if (this.customSize) {
        item.size = this.size;

        this.$emit("add-custom-item", item);
      }
    },
    getVariant() {
      this.setLoading(true);

      this.$axios
        .post("/shop/quick_quote/variant.json", {
          primary_dimension: this.primaryDimension,
          variant_ids: this.$store.state.variantIds["primaryDimension"]
        })
        .then(response => {
          this.variant = response.data.data;
          this.setLoading(false);
        });
    },
    getVariantFabricated() {
      this.setLoading(true);

      this.$axios
        .post("/shop/quick_quote/variant_fabricated.json", {
          primary_dimension: this.primaryDimension,
          variant_ids: this.$store.state.variantIds["primaryDimension"]
        })
        .then(response => {
          this.variant = response.data.data;
          this.setLoading(false);
        });
    },
    resetForm() {
      this.metalType = "";
      this.quantity = null;
      this.size = {
        depth: 0,
        type: "stock",
        width: 0
      };
      this.uom = "lbs";
    }
  }
};
</script>
